/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import {
  Container, Typography, Box, Grid, LinearProgress, CircularProgress, Chip
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ConstructionIcon from '@mui/icons-material/Construction';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface RenovationContent {
  title: string;
  subtitle: string;
  description: string;
  projectsTitle: string;
  project1Name: string;
  project1Description: string;
  project2Name: string;
  project2Description: string;
  project3Name: string;
  project3Description: string;
  impactTitle: string;
  impact1: string;
  impact2: string;
  impact3: string;
  levelsTitle: string;
  level1Name: string;
  level1Amount: string;
  level2Name: string;
  level2Amount: string;
  level3Name: string;
  level3Amount: string;
}

const defaultContent: RenovationContent = {
  title: 'Temple Renovation Fund',
  subtitle: 'Help us preserve and restore our sacred temple',
  description: 'Our temple is undergoing major renovation to preserve its heritage and improve facilities for devotees. Your support will help us complete this sacred project.',
  projectsTitle: 'Current Renovation Projects',
  project1Name: 'Sanctum Inner Chamber Restoration',
  project1Description: 'Restoration of intricate stone carvings and traditional architecture in the inner sanctum.',
  project2Name: 'Ornamental Gateway Renovation',
  project2Description: 'Complete overhaul of the gateway structures with traditional designs and craftsmanship.',
  project3Name: 'Devotee Facilities Improvement',
  project3Description: 'Enhanced water systems, seating areas, and amenities for better visitor experience.',
  impactTitle: 'How Your Support Helps',
  impact1: '\u20B91000 supports heritage preservation materials',
  impact2: '\u20B95000 sponsors skilled artisan labor',
  impact3: '\u20B910000 funds complete structural renovations',
  levelsTitle: 'Donation Levels',
  level1Name: 'Heritage Supporter',
  level1Amount: '\u20B92000',
  level2Name: 'Restoration Contributor',
  level2Amount: '\u20B910000',
  level3Name: 'Renovation Champion',
  level3Amount: '\u20B950000'
};

export default function TempleRenovation() {
  const [content, setContent] = useState<RenovationContent>(defaultContent);
  const [loading, setLoading] = useState(true);

  const goalAmount = 50000000;
  const raisedAmount = 32000000;
  const progress = (raisedAmount / goalAmount) * 100;

  useEffect(() => {
    try {
      const docRef = doc(db, 'renovation_content', 'main');
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const data = docSnap.data() as RenovationContent;
            setContent(data);
          } else {
            setContent(defaultContent);
          }
        } catch (error) {
          console.error('Error processing renovation content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('Renovation Page Error:', error);
        setContent(defaultContent);
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up listener:', error);
      setContent(defaultContent);
      setLoading(false);
    }
  }, []);

  const displayContent = content || defaultContent;

  const projects = [
    { name: 'Main Sanctum Restoration', Icon: AccountBalanceIcon, gradient: 'linear-gradient(135deg, #0d9488, #0f766e)', features: ['Gold plating of the main deity', 'Structural reinforcement', 'Traditional art restoration'] },
    { name: 'Infrastructure Development', Icon: HomeWorkIcon, gradient: 'linear-gradient(135deg, #2563eb, #1d4ed8)', features: ['New visitor facilities', 'Improved parking area', 'Wheelchair accessibility'] },
    { name: 'Kalakshethram Renovation', Icon: LocalLibraryIcon, gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)', features: ['Cultural arts center', 'Traditional music and dance hall', 'Sacred arts training programs'] },
    { name: 'Festival Support', Icon: RestaurantIcon, gradient: 'linear-gradient(135deg, #059669, #047857)', features: ['Annual festival arrangements', 'Ritual procurement and materials', 'Community celebration support'] },
  ];

  const donorBenefits = [
    'Names inscribed on temple wall of honor',
    'Special blessings and certificates',
    '80G tax exemption available',
    'Major donors: Permanent recognition plaque',
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress sx={{ color: '#0d9488' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Temple Renovation — Kottayil Temple | ജീർണോദ്ധാരണം" description="Support the renovation and restoration of Kottayil Temple (Kotta-Kovilakam Kshethram). Help preserve this ancient temple in Muchukunnu for future generations." path="/donate/renovation" keywords="Kottayil temple renovation, temple restoration, temple development fund, കൊട്ടയിൽ ക്ഷേത്രം ജീർണോദ്ധാരണം, temple restoration Kerala" />
      <PageBanner title={displayContent.title} />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #134e4a 0%, #0d9488 50%, #14b8a6 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <ConstructionIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Heritage Preservation" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' }, color: '#fff', lineHeight: 1.2 }}>
                      {displayContent.subtitle}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  {displayContent.description}
                </Typography>
              </Box>
            </Box>



            {/* ══════ RENOVATION PROJECTS ══════ */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              {projects.map((p, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: '24px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(0,0,0,0.08)', '& .projIcon': { transform: 'scale(1.15) rotate(8deg)' } },
                  }}>
                    <Box sx={{ background: p.gradient, px: 2.5, pt: 2.5, pb: 4, position: 'relative', overflow: 'hidden' }}>
                      <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2, mb: 1 }}>
                        <Box className="projIcon" sx={{
                          width: 44, height: 44, borderRadius: '14px',
                          bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                          border: '2px solid rgba(255,255,255,0.25)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                        }}>
                          <p.Icon sx={{ fontSize: 22, color: '#fff' }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>{p.name}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 16, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
                    </Box>
                    <Box sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                      {p.features.map((f, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#14b8a6', flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.74rem', color: '#888' }}>{f}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ══════ DONOR RECOGNITION (Dark Slate Footer) ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(20,184,166,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3.5, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #e5c158)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <VolunteerActivismIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>
                    Donor Recognition
                  </Typography>
                </Box>

                <Grid container spacing={1.5}>
                  {donorBenefits.map((b, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 0.5 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#14b8a6', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                          {b}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

          </Grid>

          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="donate" activePath="/donate/renovation" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
